import { IGetMessengerConversationsResponse } from "@faire/web-api--source/indigofair/data/IGetMessengerConversationsResponse";
import { useCallback, useMemo } from "react";

import { useMessengerContext } from "./__internal__/MessengerContext";
import { useMessengerStoreConversationFetching } from "./__internal__/useMessengerStoreConversationFetching";
import { useMessengerStoreStoreUpdatedConversation } from "./__internal__/useMessengerStoreStoreUpdatedConversation";

/**
 * Sets the brand context for the messenger store. A "fake" conversation
 * with a brand will exist at the top of the conversation list when
 * a brand context is set.
 */
export const useMessengerStoreBrandContext = () => {
  const { setQuery, setBrandContext } = useMessengerContext() ?? {};

  const { fetchConversations } = useMessengerStoreConversationFetching();
  const { removeBrandContextConversationIfEmpty } =
    useMessengerStoreStoreUpdatedConversation();

  /**
   * Sets the conversation as the first conversation in the list.
   *
   * Useful when you're on a page that refers to this conversation
   * ie. brand page, contact, brand order
   */
  const setCurrentBrandContext = useCallback(
    async (brand?: IGetMessengerConversationsResponse.ISimpleBrand) => {
      if (
        !setBrandContext ||
        !setQuery ||
        !removeBrandContextConversationIfEmpty ||
        !fetchConversations
      ) {
        return;
      }

      let didChangeBrandContext = false;
      let previousBrandToken = undefined;
      let newContextToken = undefined;
      setBrandContext((existingBrandContext) => {
        if (brand?.token === existingBrandContext?.token) {
          previousBrandToken = existingBrandContext?.token;
          newContextToken = existingBrandContext?.token;
          return existingBrandContext;
        }

        previousBrandToken = existingBrandContext?.token;
        if (brand?.hide_chat) {
          didChangeBrandContext = existingBrandContext !== undefined;
          return undefined;
        } else {
          didChangeBrandContext = true;
          newContextToken = brand?.token;
          return brand;
        }
      });

      // If we have a brand context that we're switching from, see if the
      // conversation has no messages. If so, remove it.
      // Don't check conversationByConversationToken because a conversation
      // has to have messages to have a conversation_token
      if (previousBrandToken && didChangeBrandContext) {
        removeBrandContextConversationIfEmpty(previousBrandToken);
      }

      if (didChangeBrandContext) {
        setQuery("");

        // Only bother to re-fetch conversations if we have a new context
        // token, because otherwise the set of conversations will still
        // be valid (a subset of the previous list that had the brand
        // context active.))
        if (newContextToken) {
          await fetchConversations({
            context_token: newContextToken,
          });
        }
      }
    },
    [
      fetchConversations,
      setBrandContext,
      setQuery,
      removeBrandContextConversationIfEmpty,
    ]
  );

  return useMemo(() => ({ setCurrentBrandContext }), [setCurrentBrandContext]);
};
